import React, { useState, useEffect, useCallback } from 'react';
import {
  supportService,
  STATUS_COLORS,
  PRIORITY_COLORS,
  CATEGORIES,
  PRIORITIES,
  formatTimeAgo,
  formatDate
} from '../services/supportService';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import './Support.css';
import {
  PlusIcon,
  XMarkIcon,
  PaperAirplaneIcon,
  TicketIcon,
  ChatBubbleLeftRightIcon
} from '@heroicons/react/24/outline';

const Support = () => {
  const { user } = useAuth();

  // State
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unreadUpdates, setUnreadUpdates] = useState(0);
  const [heroImage, setHeroImage] = useState('https://images.unsplash.com/photo-1560264280-88b68371db39?w=1600&h=400&fit=crop&crop=center&q=80');

  // View states
  const [activeView, setActiveView] = useState('list'); // 'list', 'new', 'detail'
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // New ticket form
  const [newTicket, setNewTicket] = useState({
    category: 'IT',
    priority: 'medium',
    subject: '',
    description: ''
  });
  const [submitting, setSubmitting] = useState(false);

  // Comment form
  const [newComment, setNewComment] = useState('');
  const [commentSubmitting, setCommentSubmitting] = useState(false);

  // Filter
  const [statusFilter, setStatusFilter] = useState('');

  // Fetch hero image from settings
  useEffect(() => {
    const fetchHeroImage = async () => {
      try {
        const settingsData = await api.get('/settings').then(res => res.data.grouped || {}).catch(() => ({}));
        const appearanceSettings = settingsData.appearance || [];
        const heroSetting = appearanceSettings.find(s => s.setting_key === 'support_hero_image_url');
        if (heroSetting?.setting_value) {
          setHeroImage(heroSetting.setting_value);
        }
      } catch (error) {
        console.error('Error fetching hero image:', error);
      }
    };
    fetchHeroImage();
  }, []);

  // Fetch tickets
  const fetchTickets = useCallback(async () => {
    try {
      setLoading(true);
      const params = statusFilter ? { status: statusFilter } : {};
      const data = await supportService.getTickets(params);
      setTickets(data.tickets || []);
      setUnreadUpdates(data.unread_updates || 0);
    } catch (error) {
      console.error('Error fetching tickets:', error);
      toast.error(error.message || 'Failed to load tickets');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  // Initial load
  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  // Submit new ticket
  const handleSubmitTicket = async (e) => {
    e.preventDefault();

    if (!newTicket.subject.trim() || !newTicket.description.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setSubmitting(true);
      const result = await supportService.submitTicket(newTicket);

      toast.success(`Ticket ${result.ticket.ticket_number} submitted successfully!`);

      // Reset form and go to list
      setNewTicket({
        category: 'IT',
        priority: 'medium',
        subject: '',
        description: ''
      });
      setActiveView('list');
      fetchTickets();
    } catch (error) {
      console.error('Error submitting ticket:', error);
      toast.error(error.message || 'Failed to submit ticket');
    } finally {
      setSubmitting(false);
    }
  };

  // View ticket detail
  const viewTicketDetail = async (ticketNumber) => {
    try {
      setDetailLoading(true);
      setActiveView('detail');
      const data = await supportService.getTicket(ticketNumber);
      setSelectedTicket(data.ticket);
    } catch (error) {
      console.error('Error fetching ticket:', error);
      toast.error(error.message || 'Failed to load ticket');
      setActiveView('list');
    } finally {
      setDetailLoading(false);
    }
  };

  // Add comment
  const handleAddComment = async (e) => {
    e.preventDefault();

    if (!newComment.trim() || !selectedTicket) return;

    try {
      setCommentSubmitting(true);
      const result = await supportService.addComment(selectedTicket.ticket_number, newComment);

      // Add comment to list
      setSelectedTicket({
        ...selectedTicket,
        comments: [...(selectedTicket.comments || []), result.comment]
      });

      setNewComment('');
      toast.success('Comment added');
      fetchTickets(); // Refresh to update comment counts
    } catch (error) {
      console.error('Error adding comment:', error);
      toast.error(error.message || 'Failed to add comment');
    } finally {
      setCommentSubmitting(false);
    }
  };

  // Go back to list
  const backToList = () => {
    setActiveView('list');
    setSelectedTicket(null);
    setNewComment('');
  };

  // Hero style with dark gold gradient (premium dark theme)
  const heroStyle = {
    backgroundImage: `linear-gradient(135deg, rgba(212, 169, 78, 0.45) 0%, rgba(184, 150, 62, 0.4) 50%, rgba(140, 110, 40, 0.5) 100%), url('${heroImage}')`,
    backgroundSize: 'cover',
    backgroundPosition: 'center center',
    borderRadius: '12px',
    padding: '50px 40px',
    minHeight: '180px',
    marginBottom: '24px',
    color: 'white',
    boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center'
  };

  // Render new ticket form
  const renderNewTicketForm = () => (
    <div className="support-form-container">
      <div className="form-header">
        <h2>Submit Support Ticket</h2>
        <button className="close-btn" onClick={() => setActiveView('list')}>
          <XMarkIcon />
        </button>
      </div>

      <form onSubmit={handleSubmitTicket} className="support-form">
        <div className="form-row">
          <div className="form-group">
            <label>Category *</label>
            <select
              value={newTicket.category}
              onChange={(e) => setNewTicket({ ...newTicket, category: e.target.value })}
              required
            >
              {CATEGORIES.map(cat => (
                <option key={cat.value} value={cat.value}>{cat.label}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Priority *</label>
            <select
              value={newTicket.priority}
              onChange={(e) => setNewTicket({ ...newTicket, priority: e.target.value })}
              required
            >
              {PRIORITIES.map(pri => (
                <option key={pri.value} value={pri.value}>{pri.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-group">
          <label>Subject *</label>
          <input
            type="text"
            value={newTicket.subject}
            onChange={(e) => setNewTicket({ ...newTicket, subject: e.target.value })}
            placeholder="Brief description of the issue"
            maxLength={255}
            required
          />
        </div>

        <div className="form-group">
          <label>Description *</label>
          <textarea
            value={newTicket.description}
            onChange={(e) => setNewTicket({ ...newTicket, description: e.target.value })}
            placeholder="Please describe the issue in detail. Include any error messages, steps to reproduce, and what you were trying to do."
            rows={6}
            required
          />
        </div>

        <div className="form-info">
          <p>
            <strong>Submitting as:</strong> {user?.name} ({user?.email})
          </p>
        </div>

        <div className="form-actions">
          <button type="button" className="btn-secondary" onClick={() => setActiveView('list')}>
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={submitting}>
            <PaperAirplaneIcon />
            {submitting ? 'Submitting...' : 'Submit Ticket'}
          </button>
        </div>
      </form>
    </div>
  );

  // Render ticket detail
  const renderTicketDetail = () => {
    if (detailLoading) {
      return <div className="loading">Loading ticket details...</div>;
    }

    if (!selectedTicket) {
      return <div className="error">Ticket not found</div>;
    }

    const statusStyle = STATUS_COLORS[selectedTicket.status] || STATUS_COLORS.new;
    const priorityStyle = PRIORITY_COLORS[selectedTicket.priority] || PRIORITY_COLORS.medium;

    return (
      <div className="ticket-detail-container">
        <div className="detail-header">
          <button className="back-btn" onClick={backToList}>
            &larr; Back to Tickets
          </button>
          <div className="ticket-badges">
            <span className="badge status-badge" style={{ background: statusStyle.bg, color: statusStyle.text }}>
              {statusStyle.label}
            </span>
            <span className="badge priority-badge" style={{ background: priorityStyle.bg, color: priorityStyle.text }}>
              {priorityStyle.label}
            </span>
          </div>
        </div>

        <div className="detail-content">
          <div className="ticket-info-card">
            <h2>{selectedTicket.ticket_number}</h2>
            <h3>{selectedTicket.subject}</h3>

            <div className="ticket-meta">
              <div className="meta-item">
                <label>Category</label>
                <span>{CATEGORIES.find(c => c.value === selectedTicket.category)?.label || selectedTicket.category}</span>
              </div>
              <div className="meta-item">
                <label>Submitted</label>
                <span>{formatDate(selectedTicket.created_at)}</span>
              </div>
              <div className="meta-item">
                <label>Last Updated</label>
                <span>{formatDate(selectedTicket.updated_at)}</span>
              </div>
              {selectedTicket.assigned_to_name && (
                <div className="meta-item">
                  <label>Assigned To</label>
                  <span>{selectedTicket.assigned_to_name}</span>
                </div>
              )}
            </div>

            <div className="ticket-description">
              <label>Description</label>
              <p>{selectedTicket.description}</p>
            </div>
          </div>

          {/* Comments Section */}
          <div className="comments-card">
            <h4>
              <ChatBubbleLeftRightIcon />
              Updates & Comments
            </h4>

            {selectedTicket.comments && selectedTicket.comments.length > 0 ? (
              <div className="comments-list">
                {selectedTicket.comments.map(comment => (
                  <div
                    key={comment.id}
                    className={`comment-item ${comment.author_type}`}
                  >
                    <div className="comment-header">
                      <span className="comment-author">
                        {comment.author_name}
                        {comment.author_type === 'svt' && <span className="svt-badge">SVT Support</span>}
                      </span>
                      <span className="comment-time">{formatTimeAgo(comment.created_at)}</span>
                    </div>
                    <div className="comment-body">{comment.comment}</div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="no-comments">No comments yet. We'll update this ticket as we work on it.</p>
            )}

            {/* Add Comment Form - only if ticket is not closed */}
            {selectedTicket.status !== 'closed' && (
              <form className="add-comment-form" onSubmit={handleAddComment}>
                <textarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Add a comment or provide more information..."
                  rows={3}
                />
                <button type="submit" disabled={!newComment.trim() || commentSubmitting}>
                  {commentSubmitting ? 'Sending...' : 'Send Comment'}
                </button>
              </form>
            )}
          </div>

          {/* Attachments */}
          {selectedTicket.attachments && selectedTicket.attachments.length > 0 && (
            <div className="attachments-card">
              <h4>Attachments</h4>
              <div className="attachments-list">
                {selectedTicket.attachments.map(att => (
                  <a
                    key={att.id}
                    href={att.file_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="attachment-link"
                  >
                    {att.file_name}
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Render ticket list
  const renderTicketList = () => (
    <div className="ticket-list-container">
      {/* Hero Section */}
      <div style={heroStyle}>
        <h1 style={{ fontSize: 32, fontWeight: 700, marginBottom: 8, textShadow: '0 2px 4px rgba(0,0,0,0.4)' }}>
          IT Support
        </h1>
        <p style={{ fontSize: 16, opacity: 0.95, textShadow: '0 1px 2px rgba(0,0,0,0.4)' }}>
          Submit and track your support requests
        </p>
      </div>

      <div className="list-header" style={{ marginBottom: 20 }}>
        <div></div>
        <button className="btn-primary new-ticket-btn" onClick={() => setActiveView('new')}>
          <PlusIcon />
          New Ticket
        </button>
      </div>

      {/* Status Filter */}
      <div className="filter-bar">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="status-filter"
        >
          <option value="">All Tickets</option>
          <option value="new">New</option>
          <option value="open">Open</option>
          <option value="in_progress">In Progress</option>
          <option value="on_hold">On Hold</option>
          <option value="resolved">Resolved</option>
          <option value="closed">Closed</option>
        </select>

        {unreadUpdates > 0 && (
          <span className="unread-badge">
            {unreadUpdates} ticket{unreadUpdates !== 1 ? 's' : ''} with updates
          </span>
        )}
      </div>

      {/* Tickets */}
      {loading ? (
        <div className="loading">Loading tickets...</div>
      ) : tickets.length === 0 ? (
        <div className="empty-state">
          <TicketIcon className="empty-icon" />
          <h3>No tickets found</h3>
          <p>
            {statusFilter
              ? 'No tickets match your filter. Try changing the status filter.'
              : 'You haven\'t submitted any support tickets yet. Click "New Ticket" to get help!'}
          </p>
        </div>
      ) : (
        <div className="tickets-grid">
          {tickets.map(ticket => {
            const statusStyle = STATUS_COLORS[ticket.status] || STATUS_COLORS.new;
            const priorityStyle = PRIORITY_COLORS[ticket.priority] || PRIORITY_COLORS.medium;

            return (
              <div
                key={ticket.id}
                className="ticket-card"
                onClick={() => viewTicketDetail(ticket.ticket_number)}
              >
                <div className="ticket-card-header">
                  <span className="ticket-number">{ticket.ticket_number}</span>
                  <div className="ticket-badges-small">
                    <span
                      className="badge-small"
                      style={{ background: priorityStyle.bg, color: priorityStyle.text }}
                    >
                      {priorityStyle.label}
                    </span>
                    <span
                      className="badge-small"
                      style={{ background: statusStyle.bg, color: statusStyle.text }}
                    >
                      {statusStyle.label}
                    </span>
                  </div>
                </div>

                <h4 className="ticket-subject">{ticket.subject}</h4>

                <div className="ticket-card-meta">
                  <span>{CATEGORIES.find(c => c.value === ticket.category)?.label || ticket.category}</span>
                  <span>{formatTimeAgo(ticket.created_at)}</span>
                </div>

                {ticket.assigned_to_name && (
                  <div className="ticket-assigned">
                    Assigned to: {ticket.assigned_to_name}
                  </div>
                )}

                {ticket.comment_count > 0 && (
                  <div className="ticket-comments-count">
                    <ChatBubbleLeftRightIcon />
                    {ticket.comment_count} comment{ticket.comment_count !== 1 ? 's' : ''}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  // Main render
  return (
    <div className="support-page">
      {activeView === 'list' && renderTicketList()}
      {activeView === 'new' && renderNewTicketForm()}
      {activeView === 'detail' && renderTicketDetail()}
    </div>
  );
};

export default Support;
